import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/base/Button';
import { AppLayout, type PatientData } from '@/components/layout/AppLayout';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/stores/authStore';
import { medplumResourcesApi, clinicalImpressionToSoapData } from '@/lib/api/medplum-resources';
import { vitalsApi } from '@/lib/api/vitals';
import { draftsApi } from '@/lib/api/drafts';
import { investigationOrdersApi } from '@/lib/api/investigation-orders';
import {
  caseSheetSummaryApi,
  buildCaseSheetPayloadFromSession,
} from '@/lib/api/case-sheet-summary';
import { consultationRecordsApi } from '@/lib/api/consultation-records';
import { AudioRecorder, type AudioRecorderHandle } from '@/components/consultation/AudioRecorder';
import { VitalsForm } from '@/components/consultation/VitalsForm';
import { LeaveConsultationModal } from '@/components/consultation/LeaveConsultationModal';
import { ConsultationView } from '@/components/views/ConsultationView';
import { useBackNavigationGuard } from '@/lib/hooks/useBackNavigationGuard';
import { clearConsultationData } from '@/lib/stores/consultationPersistence';
import { useAudioStore } from '@/lib/stores/audioStore';
import type { LiveKitClient } from '@/lib/livekit/LiveKitClient';
import type {
  StreamingCDSSData,
  StreamingMessage,
  VitalSigns,
  CDSSSuggestion,
  SessionInfoResponse,
  AyurvedaData,
  RegistrationInfo,
  ReferenceSlokaItem,
  ClinicalAssessmentEntry,
  ClinicalAssessmentParam,
  PrescribedMedicine,
} from '@/lib/types/consultation';
import { appointmentsApi } from '@/lib/api/appointments';
import { createComponentLogger } from '@/lib/utils/logger';
import { useIsAyurveda } from '@/ayurveda';
import { calculateAge, getPatientNameFromFhir } from '@/lib/types/patient';
import type { SoapNoteData } from '@/lib/api/medplum-resources';
// Generic clinical-text helpers are shared by both Modern Medicine and Ayurveda flows.
import {
  getArrayField,
  uniqueItems,
  isInvestigationItem,
  toDisplayText,
  type InvestigationEntry,
} from '@/lib/utils/clinicalText';
// Ayurveda-specific parsing/summarization utilities live in the Ayurveda module.
import {
  summarizeChiefComplaint,
  buildAyurvedaDataFromSoapData,
  mergeAyurvedaData,
  parseAyurvedaFromConclusion,
  doshaToChipValue,
  amaToChipValue,
  filterFoodEntries,
} from '@/ayurveda/utils/parseAyurveda';

const logger = createComponentLogger('ConsultationPage');

/**
 * Re-export parseAyurvedaFromConclusion for backward compatibility.
 * CaseSheetPage historically imported it from this module.
 */
export { parseAyurvedaFromConclusion };

function mergeSymptoms(primarySymptoms: string[], fallbackSymptoms: string[]): string[] {
  const primary = Array.isArray(primarySymptoms) ? primarySymptoms : [];
  const fallback = Array.isArray(fallbackSymptoms) ? fallbackSymptoms : [];
  return uniqueItems(
    [...primary, ...fallback]
      .map((s) => (s && typeof s === 'string' ? s.trim() : ''))
      .filter(Boolean)
  );
}

export default function ConsultationPage() {
  const { consultationId } = useParams<{ consultationId?: string }>();
  const location = useLocation();

  const locationState = location.state as {
    isContinuation?: boolean;
    previousSummary?: string;
  } | null;
  const isContinuation = locationState?.isContinuation ?? false;
  const previousSummaryRaw = locationState?.previousSummary;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const isAyurveda = useIsAyurveda();

  const [hasStartedConsultation, setHasStartedConsultation] = useState(false);

  const [modelSuggestions, setModelSuggestions] = useState<string[]>([]);
  const [modelInvestigations, setModelInvestigations] = useState<(string | InvestigationEntry)[]>(
    []
  );
  const [modelDiagnoses, setModelDiagnoses] = useState<string[]>([]);
  const [modelDiffDiagnoses, setModelDiffDiagnoses] = useState<string[]>([]);

  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [suggestedMedications, setSuggestedMedications] = useState<string[]>([]);
  const [prescribedMedications, setPrescribedMedications] = useState<string[]>([]);

  const [dietRecommendations, setDietRecommendations] = useState<string[]>([]);
  const [lifestyleRecommendations, setLifestyleRecommendations] = useState<string[]>([]);
  const [viruddhaAhaara, setViruddhaAhaara] = useState<string[]>([]);
  const [referenceSlokas, setReferenceSlokas] = useState<ReferenceSlokaItem[]>([]);
  // Astha Sthaana Pareeksha/Prakruthi/Dosha/Aama/Diagnosis/Treatment chip
  // values, live from the CDSS agent's clinical_assessment stream.
  const [clinicalAssessment, setClinicalAssessment] = useState<ClinicalAssessmentEntry[]>([]);
  // Synchronous mirror of clinicalAssessment, read inside
  // handleClinicalAssessmentOverride to compute the multi-select toggle
  // without needing clinicalAssessment in that callback's deps.
  const clinicalAssessmentRef = useRef(clinicalAssessment);
  useEffect(() => {
    clinicalAssessmentRef.current = clinicalAssessment;
  }, [clinicalAssessment]);
  const [pendingClinicalAssessmentParameter, setPendingClinicalAssessmentParameter] =
    useState<ClinicalAssessmentParam | null>(null);
  // Structured medicines table (Medicine Name/Form/Dosage/Anupana/Timing/
  // Frequency/Duration/Additional Info), live from the CDSS agent's
  // prescribed_medicines stream — full-replace each turn like clinicalAssessment.
  const [prescribedMedicines, setPrescribedMedicines] = useState<PrescribedMedicine[]>([]);

  const latestCDSSDataRef = useRef<StreamingCDSSData | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Recording state, lifted from AudioRecorder so the "leave this
  // consultation?" guard below knows whether there's anything to lose.
  const audioRecorderRef = useRef<AudioRecorderHandle>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // True for the whole stop → send → await-agent-summary round trip, which
  // starts the instant Submit/Save Draft is clicked — isRecording/isPaused
  // both flip false immediately even though the work isn't saved yet.
  const [isSubmittingAudio, setIsSubmittingAudio] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveConfirmDurationSeconds, setLeaveConfirmDurationSeconds] = useState(0);
  // True from the moment the final summary arrives until we navigate to the
  // case sheet (pre-saves + grace delay), so a refresh in that window still warns.
  const [isFinalizingSummary, setIsFinalizingSummary] = useState(false);
  const hasUnsavedSession =
    isRecording || isPaused || isSubmittingAudio || isSavingDraft || isFinalizingSummary;

  // Guards the investigation-order auto-send in handleSaveDraft below:
  // Save Draft can be clicked multiple times per consultation, and each
  // click's create() call is fire-and-forget. Without this, two clicks in
  // quick succession can both check "is there an active order yet?" before
  // either has finished writing, and both create a separate ServiceRequest
  // for the same encounter. This tracks what was last successfully sent so
  // we only call create() again when the investigations list actually
  // changed, and blocks overlapping in-flight calls.
  const lastSentInvestigationsRef = useRef<string | null>(null);
  // Tracks whether the last successful send had to fall back to the
  // "Unknown Patient" placeholder (patient record not loaded yet at the
  // time). If so, a later save with the *same* investigations list should
  // still retry once the real name is available, instead of being skipped
  // by the investigationsKey-unchanged guard forever.
  const lastSentPatientNameWasUnknownRef = useRef(false);
  const isSendingInvestigationOrderRef = useRef(false);
  // Holds the latest investigations list if it changes while a send is
  // already in flight, so it gets sent next instead of silently dropped.
  // Carries its own patientName/patientNameKnown (captured at queue time)
  // rather than relying on the in-flight call's closure — that closure
  // still holds whatever patient state was current when *it* started,
  // which can be stale ("Unknown Patient") by the time this pending entry
  // was queued with the real name already known.
  const pendingInvestigationsRef = useRef<{
    key: string;
    list: string[];
    patientName: string | undefined;
    patientNameKnown: boolean;
  } | null>(null);

  // LiveKitClient instance, exposed by AudioRecorder once connected, used to
  // send outbound clinical-assessment chip overrides.
  const liveKitClientRef = useRef<LiveKitClient | null>(null);
  const handleLiveKitClientReady = useCallback((client: LiveKitClient) => {
    liveKitClientRef.current = client;
  }, []);
  const handleClinicalAssessmentOverride = useCallback(
    (parameter: ClinicalAssessmentParam, value: string) => {
      // Nadi/Prakruthi/Dosha are multi-select: clicking a chip toggles it in
      // or out of a delimited selection list (same comma/semicolon/slash
      // delimiters ClinicalAssessmentChipGroup already parses for display),
      // instead of replacing the whole selection like every other param.
      let valueToSend = value;
      if (parameter === 'nadi' || parameter === 'prakruthi' || parameter === 'dosha') {
        const existing = clinicalAssessmentRef.current.find((e) => e.parameter === parameter);
        const current = (existing?.value ?? '')
          .split(/[,;/]/)
          .map((v) => v.trim())
          .filter(Boolean);
        const updated = current.includes(value)
          ? current.filter((v) => v !== value) // already selected — deselect
          : [...current, value]; // not selected — select
        valueToSend = updated.join(', ');
      }

      const previousEntry = clinicalAssessmentRef.current.find((e) => e.parameter === parameter);

      // Optimistic update — instant visual feedback. The backend's re-publish
      // (a full-replace "clinical_assessment_update" delta) reconciles any
      // race, e.g. a voice turn landing between this click and the ack.
      setClinicalAssessment((prev) => {
        const next = prev.filter((e) => e.parameter !== parameter);
        if (valueToSend) {
          next.push({ parameter, value: valueToSend, source: 'manual' });
        }
        return next;
      });
      setPendingClinicalAssessmentParameter(parameter);
      liveKitClientRef.current
        ?.sendClinicalAssessmentOverride(parameter, valueToSend)
        .catch((err) => {
          logger.warn('Failed to send clinical assessment override:', err);
          // The backend never saw it, so no re-publish will reconcile: restore the
          // value shown before the click.
          setClinicalAssessment((prev) => {
            const next = prev.filter((e) => e.parameter !== parameter);
            if (previousEntry) next.push(previousEntry);
            return next;
          });
        })
        .finally(() => {
          setPendingClinicalAssessmentParameter((current) =>
            current === parameter ? null : current
          );
        });
    },
    []
  );

  // Load encounter from Medplum (only for PA flow with pre-existing Encounter)
  const {
    data: encounter,
    isLoading: isLoadingEncounter,
    isError: isErrorEncounter,
  } = useQuery({
    queryKey: ['encounters', consultationId],
    queryFn: async () => medplumResourcesApi.getEncounter(consultationId!),
    enabled: !!consultationId,
    staleTime: 10000,
  });

  const patientId = encounter?.subject?.reference?.split('/')[1];

  // Effective encounter (encounter from PA flow)
  const effectiveEncounterId = consultationId || encounter?.id;

  // Derive appointmentId from encounter.appointment reference.
  // Use .pop() to handle both relative ("Appointment/123") and absolute ("https://.../Appointment/123") references.
  const appointmentIdFromEncounter = encounter?.appointment?.[0]?.reference?.split('/').pop();
  const effectiveAppointmentId = appointmentIdFromEncounter;

  // Fetch vitals for the encounter
  const { data: encounterVitals, isLoading: isLoadingVitals } = useQuery({
    queryKey: ['vitals', consultationId, patientId],
    queryFn: async () => {
      if (!patientId) return null;
      return vitalsApi.getVitalsForEncounter(consultationId!, patientId);
    },
    enabled: !!consultationId && !!patientId,
    staleTime: 60000,
  });

  const {
    data: patient,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
  } = useQuery({
    queryKey: ['patients', patientId],
    queryFn: async () => medplumResourcesApi.getPatient(patientId!),
    enabled: !!patientId,
    staleTime: 60000,
  });

  const { data: preConsultSOAP, isLoading: isLoadingSoap } = useQuery({
    queryKey: ['preConsultSOAP', consultationId],
    queryFn: () => medplumResourcesApi.getLatestCompositionForEncounter(consultationId!),
    enabled: !!consultationId && !!encounter,
    staleTime: 60000,
  });

  const linkedAppointmentRef = encounter?.appointment?.[0]?.reference;
  const { data: kioskSOAP } = useQuery<SoapNoteData | null>({
    queryKey: ['kioskClinicalImpression', linkedAppointmentRef],
    queryFn: async () => {
      if (!linkedAppointmentRef) return null;
      const apptId = linkedAppointmentRef.split('/')[1];
      const appt = await appointmentsApi.getAppointment(apptId);
      const ciRef = (appt.supportingInformation ?? []).find((r: { reference?: string }) =>
        r.reference?.startsWith('ClinicalImpression/')
      );
      if (!ciRef?.reference) return null;
      const ciId = ciRef.reference.split('/')[1];
      const ci = await medplumResourcesApi.getClinicalImpression(ciId);
      if (!ci) return null;
      return clinicalImpressionToSoapData(ci);
    },
    enabled: !!linkedAppointmentRef && !isLoadingSoap && !preConsultSOAP,
    staleTime: 60000,
  });

  const soapData = preConsultSOAP ?? kioskSOAP ?? null;

  const { data: linkedAppointment } = useQuery({
    queryKey: ['linkedAppointment', linkedAppointmentRef],
    queryFn: async () => {
      if (!linkedAppointmentRef) return null;
      const apptId = linkedAppointmentRef.split('/')[1];
      return appointmentsApi.getAppointment(apptId);
    },
    enabled: !!linkedAppointmentRef,
    staleTime: 30000,
  });
  const [ayurvedaData, setAyurvedaData] = useState<AyurvedaData | null>(null);
  const compositionAyurvedaData = useMemo(
    () => buildAyurvedaDataFromSoapData(soapData),
    [soapData]
  );
  const mergedAyurvedaData = useMemo(
    () => mergeAyurvedaData(compositionAyurvedaData, ayurvedaData),
    [compositionAyurvedaData, ayurvedaData]
  );

  // Registration fields normally come from the PA (kiosk) agent's Composition
  // "Registration" section, but that section is absent for consultations that
  // never had a pre-consult PA session. Fall back to the same FHIR Patient
  // fields the Case Sheet's "Patient Details" popover already uses
  // successfully, so the Registration card isn't left blank.
  const fhirRegistrationFallback: RegistrationInfo | null = useMemo(() => {
    if (!patient) return null;
    return {
      opNo: patient.identifier?.[0]?.value || null,
      date: null,
      legalName: getPatientNameFromFhir(patient.name) || null,
      age: patient.birthDate ? `${calculateAge(patient.birthDate)} years` : null,
      gender: patient.gender ? patient.gender.toUpperCase() : null,
      address: null,
      phone: patient.telecom?.find((t) => t.system === 'phone')?.value || null,
      email: patient.telecom?.find((t) => t.system === 'email')?.value || null,
    };
  }, [patient]);

  // Fallback only — the `history_information_update` live delta (handled in
  // handleCDSSData below) is the primary live source for chiefComplaint,
  // covering both kiosk and no-kiosk consultations. This only fills the gap
  // for the window before that delta has fired at all (e.g. right at the
  // start of a pure live, no-kiosk consultation): `symptoms` is already
  // streamed live by the CDSS agent every turn, so treat it as a rough
  // stand-in chief complaint until the richer delta arrives.
  const liveChiefComplaintFallback = useMemo(
    () => (symptoms.length > 0 ? symptoms.join(', ') : null),
    [symptoms]
  );

  const effectiveAyurvedaData = useMemo(() => {
    const registration = mergedAyurvedaData?.registration ?? fhirRegistrationFallback;
    const chiefComplaint = mergedAyurvedaData?.chiefComplaint ?? liveChiefComplaintFallback ?? null;
    if (!mergedAyurvedaData) {
      return registration || chiefComplaint
        ? ({ registration, chiefComplaint } as AyurvedaData)
        : null;
    }
    return { ...mergedAyurvedaData, registration, chiefComplaint };
  }, [mergedAyurvedaData, fhirRegistrationFallback, liveChiefComplaintFallback]);

  // Always mirrors the latest effectiveAyurvedaData. The Submit handler is
  // async (several awaited DB saves before it navigates to the Case Sheet) —
  // reading effectiveAyurvedaData directly from its closure would use a
  // snapshot from when the handler started, missing anything the doctor said
  // (e.g. treatment/medicines) right as they clicked Submit, while those
  // saves are still in flight. Reading this ref instead, right before
  // navigating, picks up whatever is freshest at that moment.
  const effectiveAyurvedaDataRef = useRef(effectiveAyurvedaData);
  useEffect(() => {
    effectiveAyurvedaDataRef.current = effectiveAyurvedaData;
  }, [effectiveAyurvedaData]);

  // The PA (kiosk) Composition is parsed once into base-snapshot fields on
  // AyurvedaData (asthaSthaanaPareeksha, prakruthiDosha, diagnosis, treatment)
  // but AyurvedicAssessmentCard/AITranscribedDataCard only ever read from the
  // live `clinicalAssessment` array (the CDSS agent's clinical_assessment
  // stream, which only starts producing values once the doctor's live
  // consultation actually discusses them) — so right after the kiosk step,
  // before the doctor has said anything, these cards were left blank even
  // though the kiosk already captured this data. Surface the snapshot as a
  // fallback for every one of these parameters; any live entry (voice/manual)
  // always takes priority over it.
  const assessmentSnapshotFallback: ClinicalAssessmentEntry[] = useMemo(() => {
    const entries: ClinicalAssessmentEntry[] = [];
    const astha = effectiveAyurvedaData?.asthaSthaanaPareeksha;
    const prakruthiDosha = effectiveAyurvedaData?.prakruthiDosha;
    const diagnosis = effectiveAyurvedaData?.diagnosis;
    const treatment = effectiveAyurvedaData?.treatment;
    const doshaProfile = effectiveAyurvedaData?.doshaProfile;
    const ama = effectiveAyurvedaData?.ama;

    // The "Prakruthi Dosha" JSON snapshot doesn't exist in the newer PA
    // schema (no `prakruthi_dosha` section is emitted at all) — the closest
    // real equivalent is the "Ayurveda Context" JSON's doshaProfile.prakriti/
    // .vikriti and ama fields, already parsed by parseAyurveda.ts but never
    // surfaced anywhere. Map those free-text descriptions onto the fixed
    // Prakruthi/Dosha/Aama chip option strings so the chip UI actually
    // highlights what the SOAP notes recorded, only when the old-schema
    // snapshot has nothing for that specific field.
    const prakruthiValue = prakruthiDosha?.prakruthi ?? doshaToChipValue(doshaProfile?.prakriti);
    const doshaValue = prakruthiDosha?.dosha ?? doshaToChipValue(doshaProfile?.vikriti);
    const aamaValue = prakruthiDosha?.aama ?? amaToChipValue(ama);

    const asthaFields: Array<[ClinicalAssessmentParam, string | null | undefined]> = [
      ['nadi', astha?.nadi],
      ['mootram', astha?.mootram],
      ['malam', astha?.malam],
      ['jihva', astha?.jihva],
      ['sabdam', astha?.sabdam],
      ['sparsham', astha?.sparsham],
      ['drik', astha?.drik],
      ['akrithi', astha?.akrithi],
      ['prakruthi', prakruthiValue],
      ['dosha', doshaValue],
      ['aama', aamaValue],
    ];
    for (const [parameter, value] of asthaFields) {
      if (value) entries.push({ parameter, value, source: 'pa_soap' });
    }

    if (diagnosis?.ayurvedic) {
      entries.push({
        parameter: 'diagnosis_ayurvedic',
        value: diagnosis.ayurvedic,
        source: 'pa_soap',
      });
    }
    if (diagnosis?.allopathic) {
      entries.push({
        parameter: 'diagnosis_allopathic',
        value: diagnosis.allopathic,
        source: 'pa_soap',
      });
    }
    if (Array.isArray(treatment?.medicines) && treatment.medicines.length > 0) {
      entries.push({
        parameter: 'treatment_medicines',
        value: treatment.medicines.join(', '),
        source: 'pa_soap',
      });
    }
    if (treatment?.treatmentPrinciple) {
      entries.push({
        parameter: 'treatment_principle',
        value: treatment.treatmentPrinciple,
        source: 'pa_soap',
      });
    }
    if (treatment?.treatmentGiven) {
      entries.push({
        parameter: 'treatment_given',
        value: treatment.treatmentGiven,
        source: 'pa_soap',
      });
    }
    return entries;
  }, [
    effectiveAyurvedaData?.asthaSthaanaPareeksha,
    effectiveAyurvedaData?.prakruthiDosha,
    effectiveAyurvedaData?.diagnosis,
    effectiveAyurvedaData?.treatment,
    effectiveAyurvedaData?.doshaProfile,
    effectiveAyurvedaData?.ama,
  ]);

  const effectiveClinicalAssessment: ClinicalAssessmentEntry[] = useMemo(() => {
    const liveParameters = new Set(clinicalAssessment.map((entry) => entry.parameter));
    const fallbackOnly = assessmentSnapshotFallback.filter(
      (entry) => !liveParameters.has(entry.parameter)
    );
    return [...clinicalAssessment, ...fallbackOnly];
  }, [clinicalAssessment, assessmentSnapshotFallback]);

  // Same reasoning as effectiveAyurvedaDataRef above.
  const effectiveClinicalAssessmentRef = useRef(effectiveClinicalAssessment);
  useEffect(() => {
    effectiveClinicalAssessmentRef.current = effectiveClinicalAssessment;
  }, [effectiveClinicalAssessment]);

  // Same reasoning as effectiveClinicalAssessmentRef above — read at Submit
  // time via .current so a medicine said right before Submit isn't lost.
  const prescribedMedicinesRef = useRef(prescribedMedicines);
  useEffect(() => {
    prescribedMedicinesRef.current = prescribedMedicines;
  }, [prescribedMedicines]);

  // Mutation to update encounter status to 'in-progress' (only for PA flow)
  const startConsultationMutation = useMutation({
    mutationFn: () =>
      medplumResourcesApi.updateEncounter(consultationId!, { status: 'in-progress' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', consultationId] });
      setHasStartedConsultation(true);
    },
    onError: (error) => {
      logger.error('Failed to start consultation', error);
      toast.error('Unable to initialize consultation. Please try again.', { duration: 3000 });
    },
  });

  useEffect(() => {
    if (encounter && patient && !isLoadingEncounter && !isLoadingPatient) {
      toast.success('Ready to start consultation', { duration: 2000 });
    }
  }, [encounter?.id, patient?.id]);

  useEffect(() => {
    if (encounter?.status === 'planned' && !hasStartedConsultation) {
      startConsultationMutation.mutate();
    }
  }, [encounter?.status, hasStartedConsultation]);

  const activePatient = patient
    ? {
        patient: {
          uuid: patient.id || '',
          identifier: patient.id || '',
          name: getPatientNameFromFhir(patient.name) || 'Unknown Patient',
          age: patient.birthDate ? calculateAge(patient.birthDate) : undefined,
          gender: patient.gender?.toUpperCase() || 'U',
        },
      }
    : null;

  // Consultation is considered done when the linked appointment is fulfilled
  const isFinished = linkedAppointment?.status === 'fulfilled';

  // Update vitals mutation — persists vitals entered mid-consultation as FHIR
  // Observation resources linked to this encounter.
  const updateVitalsMutation = useMutation({
    mutationFn: async (vitals: VitalSigns) => {
      if (!effectiveEncounterId || !patientId) {
        throw new Error('Missing encounter or patient — cannot save vitals');
      }
      await vitalsApi.updateVitals({
        patientId,
        encounterId: effectiveEncounterId,
        vitals,
        practitionerId: user?.id,
      });
    },
    onSuccess: () => {
      // Refresh displayed vitals after saving
      queryClient.invalidateQueries({ queryKey: ['vitals', consultationId, patientId] });
      toast.success('Vitals saved successfully');
      queryClient.invalidateQueries({ queryKey: ['vitals', effectiveEncounterId] });
    },
    onError: (error) => {
      logger.error('Failed to save vitals:', error);
      toast.error('Failed to save vitals');
    },
  });

  // Field-by-field merge of an incoming personal_history payload over whatever
  // is already known (kiosk snapshot or earlier live values). Works with no
  // base object at all (pure live CDSS session, no kiosk step).
  const applyPersonalHistory = useCallback(
    (incoming: NonNullable<StreamingCDSSData['personal_history']>) => {
      setAyurvedaData((prev) => {
        const current = prev ?? compositionAyurvedaData ?? null;
        const prevPersonalHistory = current?.personalHistory ?? null;
        return {
          ...(current ?? {}),
          personalHistory: {
            diet: prevPersonalHistory?.diet ?? null,
            appetite: incoming.appetite ?? prevPersonalHistory?.appetite ?? null,
            sleep: incoming.sleep ?? prevPersonalHistory?.sleep ?? null,
            exercise: incoming.exercise ?? prevPersonalHistory?.exercise ?? null,
            bowel: incoming.bowel ?? prevPersonalHistory?.bowel ?? null,
            bladder: incoming.bladder ?? prevPersonalHistory?.bladder ?? null,
            addiction: incoming.addiction ?? prevPersonalHistory?.addiction ?? null,
            menstrual: incoming.menstrual ?? prevPersonalHistory?.menstrual ?? null,
          },
        };
      });
    },
    [compositionAyurvedaData]
  );

  const handleCDSSData = useCallback(
    (data: StreamingCDSSData) => {
      logger.log('CDSS Data:', data);
      if (isAyurveda) {
        logger.log('CDSS payload:', data);
        logger.log('Suggestive slokas:', data.suggestive_slokas);
        logger.log('CDSS Personal History / Astha keys:', {
          personal_history: !!data.personal_history,
          personal_history_update: !!data.personal_history_update,
          clinical_assessment: Array.isArray(data.clinical_assessment),
          clinical_assessment_update: !!data.clinical_assessment_update,
        });
      }

      // Structured medicines table — Ayurveda-only, full-replace whenever
      // present, independent of which branch below this payload falls into
      // (mirrors clinical_assessment's full-replace semantics, but isn't
      // gated behind its own "_update" delta flag since the backend always
      // sends the complete table together with whichever payload carries it).
      if (isAyurveda && Array.isArray(data.prescribed_medicines)) {
        setPrescribedMedicines(data.prescribed_medicines);
      }

      // Clinical-assessment-only delta update (a manual chip override
      // re-broadcast on `session_info` — see LiveKitClient's
      // clinical_assessment_update branches). This payload carries ONLY
      // clinical_assessment, so — same rationale as the sloka delta below —
      // it must return early instead of falling through to the "replace with
      // latest" logic further down, which would otherwise wipe every other
      // CDSS field to empty.
      if (data.clinical_assessment_update) {
        if (Array.isArray(data.clinical_assessment)) {
          setClinicalAssessment(data.clinical_assessment);
        }
        return;
      }

      // Desa-information-only delta update (location → weather + regional
      // diet, Environmental Profile panel) — arrives once the CDSS agent's
      // debounced background lookup resolves after the patient states their
      // city. Same early-return rationale as the clinical_assessment delta
      // above: this payload carries ONLY desa_information, so falling
      // through would wipe every other CDSS field to empty.
      //
      // Unlike the family_history/known_diseases merge below (which bails
      // out entirely when there's no prior composition/ayurvedaData), this
      // must work even with no base object at all — a client can go
      // straight into a CDSS consultation with no kiosk/Composition step
      // ever having run, so `current` being null is the expected case, not
      // an edge case to skip.
      if (data.desa_information_update && data.desa_information) {
        const incoming = data.desa_information;
        setAyurvedaData((prev) => {
          const current = prev ?? compositionAyurvedaData ?? null;
          const prevDesa = current?.desaInformation ?? null;
          return {
            ...(current ?? {}),
            desaInformation: {
              city: incoming.city ?? prevDesa?.city ?? null,
              state: incoming.state ?? prevDesa?.state ?? null,
              season: incoming.season ?? prevDesa?.season ?? null,
              desaType: incoming.desa_type ?? prevDesa?.desaType ?? null,
              temperature:
                typeof incoming.temperature === 'number'
                  ? incoming.temperature
                  : (prevDesa?.temperature ?? null),
              humidity:
                typeof incoming.humidity === 'number'
                  ? incoming.humidity
                  : (prevDesa?.humidity ?? null),
              stapleDiet: incoming.staple_diet ?? prevDesa?.stapleDiet ?? null,
              regionalDiet: incoming.regional_diet
                ? {
                    grains: incoming.regional_diet.grains ?? '',
                    pulses: incoming.regional_diet.pulses ?? '',
                    fruits: incoming.regional_diet.fruits ?? '',
                    veg: incoming.regional_diet.veg ?? '',
                    dairy: incoming.regional_diet.dairy ?? '',
                    spiceLevel:
                      typeof incoming.regional_diet.spice_level === 'number'
                        ? incoming.regional_diet.spice_level
                        : null,
                  }
                : (prevDesa?.regionalDiet ?? null),
            },
          };
        });
        return;
      }

      // Personal-history-only delta update — same early-return rationale as
      // the desa-information delta above. Carries appetite/sleep/exercise/
      // bowel/bladder/addiction/menstrual (no `diet` key — that's sourced
      // separately, from the kiosk composition if any). Each field falls
      // back to whatever was already known (kiosk snapshot) when this delta
      // doesn't carry a value for it.
      if (data.personal_history_update && data.personal_history) {
        applyPersonalHistory(data.personal_history);
        return;
      }

      // History-information delta update (chief complaint, duration, mode of
      // onset, and the History section fields) — same early-return rationale
      // as the desa-information/personal-history deltas above: this must work
      // even with no prior composition/ayurvedaData (a pure live CDSS
      // session, no kiosk step), and only overrides the fields this delta
      // actually carries, leaving everything else on `current` untouched.
      if (data.history_information_update && data.history_information) {
        const incoming = data.history_information;
        setAyurvedaData((prev) => {
          const current = prev ?? compositionAyurvedaData ?? null;
          return {
            ...(current ?? {}),
            chiefComplaint: incoming.chief_complaint ?? current?.chiefComplaint ?? null,
            duration: incoming.duration ?? current?.duration ?? null,
            modeOfOnset: incoming.mode_of_onset ?? current?.modeOfOnset ?? null,
            history: incoming.history ?? current?.history ?? null,
            pastIllness: incoming.past_illness ?? current?.pastIllness ?? null,
            treatmentHistory: incoming.treatment_history ?? current?.treatmentHistory ?? null,
            historyFamilyHistory: incoming.family_history ?? current?.historyFamilyHistory ?? null,
            familySocialOccupational:
              incoming.family_social_occupational ?? current?.familySocialOccupational ?? null,
            currentMedication: incoming.current_medication ?? current?.currentMedication ?? null,
          };
        });
        return;
      }

      // Clinical assessment carried on a regular (non-delta) CDSS turn —
      // full-replace, same as the rest of the middle/right column below. The
      // backend already resolves the sticky pa_soap/manual lock server-side,
      // so no merge logic is needed here.
      if (Array.isArray(data.clinical_assessment)) {
        setClinicalAssessment(data.clinical_assessment);
      }

      // Personal history carried on a regular (non-delta) turn — apply it too,
      // but don't return: the rest of this turn's CDSS fields still need to
      // be processed below.
      if (data.personal_history) {
        applyPersonalHistory(data.personal_history);
      }

      // Sloka-only delta update from the live grounding background worker.
      // This arrives on the same `session_info` channel but should NOT reset
      // the rest of the middle/right-panel streaming state.
      if (data.suggestive_slokas_update) {
        if (Array.isArray(data.suggestive_slokas)) {
          const receivedAt = new Date().toISOString();
          logger.log(
            `[SlokaDebug] [${receivedAt}] Suggestive slokas delta update received in ConsultationPage`,
            {
              count: data.suggestive_slokas.length,
              firstTitle: data.suggestive_slokas[0]?.title,
            }
          );
          setReferenceSlokas(data.suggestive_slokas);
          logger.log(
            `[SlokaDebug] [${new Date().toISOString()}] ConsultationPage state update queued for suggestive slokas`,
            {
              count: data.suggestive_slokas.length,
            }
          );
        }
        return;
      }

      // Store latest CDSS data for draft saving
      latestCDSSDataRef.current = data;

      setModelSuggestions(
        Array.isArray(data.follow_up_questions) ? data.follow_up_questions.slice(0, 5) : []
      );
      setModelInvestigations(
        Array.isArray(data.additional_suggestions) ? data.additional_suggestions.slice(0, 5) : []
      );
      setModelDiagnoses(
        Array.isArray(data.possible_diagnoses) ? data.possible_diagnoses.slice(0, 5) : []
      );
      setModelDiffDiagnoses(
        Array.isArray(data.confirmed_diagnoses) ? data.confirmed_diagnoses.slice(0, 5) : []
      );

      setSymptoms(getArrayField(data.symptoms));
      // Normalize medication arrays: backend may send objects {medication, medication_name, notes} or plain strings
      const normalizeMedication = (m: unknown): string =>
        typeof m === 'string'
          ? m
          : (m as any)?.medication ||
            (m as any)?.medication_name ||
            (m as any)?.name ||
            JSON.stringify(m);

      setSuggestedMedications(
        Array.isArray(data.suggested_medications)
          ? data.suggested_medications.slice(0, 5).map(normalizeMedication)
          : []
      );
      setPrescribedMedications(
        Array.isArray(data.prescribed_medications)
          ? data.prescribed_medications.slice(0, 5).map(normalizeMedication)
          : []
      );
      setDietRecommendations(
        Array.isArray(data.diet_recommendations)
          ? data.diet_recommendations.map(normalizeMedication)
          : []
      );
      setLifestyleRecommendations(
        Array.isArray(data.lifestyle_recommendations)
          ? data.lifestyle_recommendations.map(normalizeMedication)
          : []
      );
      setViruddhaAhaara(getArrayField(data.viruddha_ahaara));
      // Suggestive slokas — KB-enriched Ayurveda references streamed live by the CDSS agent.
      // Only replace when the stream carries slokas so a later empty turn doesn't wipe
      // references gathered earlier in the consultation.
      if (Array.isArray(data.suggestive_slokas) && data.suggestive_slokas.length > 0) {
        const receivedAt = new Date().toISOString();
        logger.log(
          `[SlokaDebug] [${receivedAt}] Suggestive slokas received in regular CDSS payload`,
          {
            count: data.suggestive_slokas.length,
            firstTitle: data.suggestive_slokas[0]?.title,
          }
        );
        setReferenceSlokas(data.suggestive_slokas);
        logger.log(
          `[SlokaDebug] [${new Date().toISOString()}] ConsultationPage state update queued for suggestive sloka payload`,
          {
            count: data.suggestive_slokas.length,
          }
        );
      }

      const streamedFamilyHistory = Array.isArray(data.family_history) ? data.family_history : [];
      const streamedKnownDiseases = Array.isArray(data.known_diseases) ? data.known_diseases : [];
      const streamedCommonlyEatenFood = filterFoodEntries(data.commonly_eaten_food);
      if (
        streamedFamilyHistory.length ||
        streamedKnownDiseases.length ||
        streamedCommonlyEatenFood.length
      ) {
        setAyurvedaData((prev) => {
          const current = prev ?? compositionAyurvedaData;
          if (!current) return prev;
          return {
            ...current,
            familyHistory: streamedFamilyHistory.length
              ? streamedFamilyHistory
              : current.familyHistory,
            knownDiseases: streamedKnownDiseases.length
              ? streamedKnownDiseases
              : current.knownDiseases,
            commonlyEatenFood: streamedCommonlyEatenFood.length
              ? streamedCommonlyEatenFood
              : current.commonlyEatenFood,
          };
        });
      }
    },
    [compositionAyurvedaData, applyPersonalHistory, isAyurveda]
  );

  /**
   * Send/sync ordered investigations to the Investigation Orders workflow.
   * Called from Save Draft. This is a side-effect, not the primary action —
   * a failure here must not block or fail the caller.
   */
  const sendInvestigationOrderFromSummary = useCallback(
    (summary: SessionInfoResponse['summary']) => {
      if (!patientId || !user?.id || !consultationId) return;
      if (consultationId.startsWith('session-')) return;

      // investigations_orders follows the same "flexible field" shape
      // documented on FlexibleSessionSummaryField elsewhere in this
      // codebase — it can arrive as a bare string, a bare array, or the
      // { value: ... } wrapper, and each entry isn't always a plain
      // string either: the AI agent now also captures them as
      // { test, value, unit, notes? } objects (see InvestigationEntry).
      // Normalize both layers before building the payload.
      const rawInvestigations = summary.investigations_orders as unknown;
      const rawInvestigationsValue =
        rawInvestigations &&
        typeof rawInvestigations === 'object' &&
        !Array.isArray(rawInvestigations)
          ? (rawInvestigations as { value?: unknown }).value
          : rawInvestigations;
      const investigationsList: unknown[] = Array.isArray(rawInvestigationsValue)
        ? rawInvestigationsValue
        : typeof rawInvestigationsValue === 'string'
          ? [rawInvestigationsValue]
          : [];
      const investigations = investigationsList
        .map((item) => {
          if (typeof item === 'string') return item.trim();
          if (isInvestigationItem(item)) return toDisplayText(item.test);
          return '';
        })
        .filter(Boolean);
      const investigationsKey = investigations.length ? investigations.join('|') : null;
      if (!investigationsKey) return;

      const currentPatientName = activePatient?.patient.name;
      const isPatientNameKnown = !!currentPatientName && currentPatientName !== 'Unknown Patient';

      const sendInvestigationOrder = (
        key: string,
        list: string[],
        patientName: string | undefined,
        patientNameKnown: boolean
      ) => {
        isSendingInvestigationOrderRef.current = true;
        investigationOrdersApi
          .create({
            patientId,
            patientName: patientName || 'Unknown Patient',
            practitionerId: user.id,
            practitionerName:
              `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown Practitioner',
            encounterId: consultationId,
            investigations: list,
          })
          .then(() => {
            lastSentInvestigationsRef.current = key;
            lastSentPatientNameWasUnknownRef.current = !patientNameKnown;
          })
          .catch((err) => logger.error('Failed to send investigation order:', err))
          .finally(() => {
            isSendingInvestigationOrderRef.current = false;
            // If a newer investigations list arrived while this request
            // was in flight, it was parked in pendingInvestigationsRef
            // instead of being dropped — send it now, using the patient
            // state captured at the time it was queued (not this
            // request's own, possibly stale, closure).
            const pending = pendingInvestigationsRef.current;
            pendingInvestigationsRef.current = null;
            if (pending && pending.key !== lastSentInvestigationsRef.current) {
              sendInvestigationOrder(
                pending.key,
                pending.list,
                pending.patientName,
                pending.patientNameKnown
              );
            }
          });
      };

      // Re-send when the investigations list actually changed, OR when
      // the previous send had to use the "Unknown Patient" placeholder
      // (patient record hadn't loaded yet) and the real name is now
      // known — otherwise that placeholder would be stuck on the order
      // forever, since the investigationsKey-unchanged guard would
      // silently skip every later call.
      const investigationsChanged = investigationsKey !== lastSentInvestigationsRef.current;
      const shouldCorrectPatientName =
        investigationsKey === lastSentInvestigationsRef.current &&
        lastSentPatientNameWasUnknownRef.current &&
        isPatientNameKnown;

      if (!investigationsChanged && !shouldCorrectPatientName) return;

      if (isSendingInvestigationOrderRef.current) {
        pendingInvestigationsRef.current = {
          key: investigationsKey,
          list: investigations,
          patientName: currentPatientName,
          patientNameKnown: isPatientNameKnown,
        };
      } else {
        sendInvestigationOrder(
          investigationsKey,
          investigations,
          currentPatientName,
          isPatientNameKnown
        );
      }
    },
    [patientId, user, consultationId, activePatient]
  );

  /**
   * Save the generated consultation summary as a draft to the database and
   * navigate to dashboard. Called once the LiveKit agent's session_summary
   * arrives for a Save-Draft-triggered submission (see AudioRecorder).
   */
  const handleSaveDraft = useCallback(
    async (sessionInfo: SessionInfoResponse) => {
      if (isSavingDraft || !user?.id) return;

      if (!sessionInfo?.summary) return;

      if (!patientId) {
        toast.error('Patient information not available', { duration: 3000 });
        return;
      }

      setIsSavingDraft(true);

      try {
        // Save generated summary to database
        await draftsApi.saveDraft(consultationId!, user?.id || '', patientId, sessionInfo.summary);

        sendInvestigationOrderFromSummary(sessionInfo.summary);

        // Keep encounter as in-progress
        if (consultationId && !consultationId.startsWith('session-')) {
          try {
            await medplumResourcesApi.updateEncounter(consultationId, {
              status: 'in-progress',
            });
          } catch (e) {
            logger.error('Failed to update encounter status:', e);
          }
        }

        // Mark appointment as pending (shown as "On Hold" on the dashboard) — fetch linked appointment from encounter
        if (consultationId && !consultationId.startsWith('session-')) {
          try {
            const linkedAppointment =
              await appointmentsApi.getAppointmentForEncounter(consultationId);
            if (linkedAppointment && linkedAppointment.status !== 'fulfilled') {
              // Existing appointment found — just update its status
              await appointmentsApi.updateAppointment(linkedAppointment.id, {
                status: 'pending',
                // Adds the Appointment -> Encounter link if it is missing; the dashboard
                // only offers Continue / locks deletion for appointments carrying it.
                encounterId: consultationId,
              });
            } else if (!linkedAppointment) {
              // No appointment exists — create one so
              // the dashboard shows a "Continue" button for this draft
              const now = new Date();
              const endOfDay = new Date(now);
              endOfDay.setHours(23, 59, 59, 999);

              const pName = patient
                ? `${patient.name?.[0]?.given?.join(' ') || ''} ${patient.name?.[0]?.family || ''}`.trim()
                : '';

              const newAppointment = await appointmentsApi.createAppointment({
                status: 'pending',
                start: now.toISOString(),
                end: endOfDay.toISOString(),
                patientId,
                practitionerId: user.id,
                appointmentType: 'Consultation',
                description: pName
                  ? `Consultation for ${pName} (on hold)`
                  : 'Consultation (on hold)',
                encounterId: consultationId,
              });

              // Link appointment back to encounter so getAppointmentForEncounter
              // finds it on subsequent "On Hold" clicks (prevents duplicates)
              if (newAppointment.id) {
                try {
                  await medplumResourcesApi.updateEncounter(consultationId, {
                    appointment: [{ reference: `Appointment/${newAppointment.id}` }],
                  });
                } catch (linkErr) {
                  logger.warn('Failed to link appointment to encounter:', linkErr);
                }
              }

              toast.info('Appointment created for this consultation.', { duration: 3000 });
            }
          } catch (e) {
            logger.warn('Could not update/create appointment for draft:', e);
          }
        }

        // Invalidate dashboard queries so "Continue" button appears
        queryClient.invalidateQueries({
          queryKey: ['dashboard-practitioner-appointments'],
          exact: false,
        });
        queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });

        toast.success('Draft saved. You can continue the consultation later.', {
          duration: 3000,
        });
        // replace: true — don't leave a stale consultation history entry
        // behind (see useBackNavigationGuard) that Back from the dashboard
        // would remount into a fresh auto-started recording.
        navigate('/dashboard', { replace: true });
      } catch (error) {
        logger.error('Failed to save draft:', error);
        toast.error('Failed to save draft. Please try again.', { duration: 3000 });
      } finally {
        setIsSavingDraft(false);
      }
    },
    [
      isSavingDraft,
      consultationId,
      patientId,
      user?.id,
      patient,
      queryClient,
      navigate,
      sendInvestigationOrderFromSummary,
    ]
  );

  /**
   * Save consultation record to DB immediately when Save Record is clicked
   */
  const handleSaveRecord = useCallback(
    (isQuickSubmit?: boolean) => {
      if (!consultationId || !patientId) return;

      const pName = activePatient?.patient.name || 'Unknown Patient';
      const prName =
        `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown Practitioner';
      const cdssDataToSave: Record<string, unknown> = { ...(latestCDSSDataRef.current || {}) };

      // Mark this record as Quick Submitted inside the existing pranikCdss
      // JSON blob — there's no dedicated column for this, and the backend
      // already stores/reads pranikCdss verbatim, so this needs no schema
      // change. The dashboard reads this back via caseSheetSummaryApi to
      // distinguish a Quick Submit from a normal Submit (both otherwise
      // create the same kind of record).
      if (isQuickSubmit) {
        cdssDataToSave.pranikCdss = {
          ...(cdssDataToSave.pranikCdss as Record<string, unknown> | undefined),
          quickSubmit: true,
        };
      }

      // Fire and forget — don't block the UI
      consultationRecordsApi
        .saveRecord(
          consultationId,
          user?.id || '',
          patientId,
          pName,
          prName,
          new Date().toISOString(),
          cdssDataToSave as StreamingCDSSData
        )
        .then(() => {
          logger.log('Consultation record saved successfully');
        })
        .catch((e) => {
          logger.warn('Failed to save consultation record:', e);
        });
    },
    [
      consultationId,
      patientId,
      activePatient?.patient.name,
      user?.firstName,
      user?.lastName,
      user?.id,
    ]
  );

  /**
   * Quick Submit — fired once AudioRecorder has sent session_quick_end.
   * No summary arrives on this path (the agent writes it directly to
   * Medplum in the background), so we navigate to the dashboard right
   * away instead of waiting on handleSessionSummary.
   */
  const handleQuickSubmit = useCallback(() => {
    // Marks this encounter as "just Quick Submitted" so the dashboard's
    // status check treats a race with the fire-and-forget local save (which
    // can otherwise 404 and look like "not a Quick Submit") as "still
    // preparing" instead of silently giving up on this row. Expires after 2
    // minutes inside the dashboard's own check.
    if (consultationId) {
      queryClient.setQueryData(['quick-submit-just-fired', consultationId], Date.now());
    }

    queryClient.invalidateQueries({
      queryKey: ['dashboard-practitioner-appointments'],
      exact: false,
    });
    queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['dashboard-summary-status'], exact: false });

    toast.success('Consultation submitted. The summary will be ready to verify shortly.', {
      duration: 3000,
    });
    // replace: true — see the matching note in handleSaveDraft above.
    navigate('/dashboard', { replace: true });
  }, [navigate, queryClient, consultationId]);

  const handleCDSSSuggestion = useCallback(
    (message: StreamingMessage) => {
      const extractJsonObjects = (text: string): unknown[] => {
        const objs: unknown[] = [];
        let depth = 0;
        let start = -1;
        for (let i = 0; i < text.length; i++) {
          const ch = text[i];
          if (ch === '{') {
            if (depth === 0) start = i;
            depth++;
          } else if (ch === '}') {
            depth--;
            if (depth === 0 && start !== -1) {
              const chunk = text.substring(start, i + 1);
              try {
                objs.push(JSON.parse(chunk));
              } catch (e) {
                logger.warn('Failed to parse JSON chunk from suggestion', e);
              }
              start = -1;
            }
          }
        }
        return objs;
      };

      const objs = extractJsonObjects(message.text);
      if (objs.length > 0) {
        const modelObj: any = objs.find(
          (o: any) =>
            o &&
            (o.possible_diagnoses ||
              o.follow_up_questions ||
              o.additional_suggestions ||
              o.suggested_medications ||
              o.symptoms ||
              o.diet_recommendations ||
              o.lifestyle_recommendations ||
              o.viruddha_ahaara ||
              o.family_history ||
              o.known_diseases ||
              o.commonly_eaten_food ||
              o.suggestive_slokas)
        );
        if (modelObj) {
          const data: StreamingCDSSData = {
            possible_diagnoses: Array.isArray(modelObj.possible_diagnoses)
              ? modelObj.possible_diagnoses
              : [],
            follow_up_questions: Array.isArray(modelObj.follow_up_questions)
              ? modelObj.follow_up_questions
              : [],
            additional_suggestions: Array.isArray(modelObj.additional_suggestions)
              ? modelObj.additional_suggestions
              : [],
            suggested_medications: Array.isArray(modelObj.suggested_medications)
              ? modelObj.suggested_medications
              : [],
            symptoms: Array.isArray(modelObj.symptoms) ? modelObj.symptoms : [],
            diet_recommendations: Array.isArray(modelObj.diet_recommendations)
              ? modelObj.diet_recommendations
              : [],
            lifestyle_recommendations: Array.isArray(modelObj.lifestyle_recommendations)
              ? modelObj.lifestyle_recommendations
              : [],
            viruddha_ahaara: Array.isArray(modelObj.viruddha_ahaara)
              ? modelObj.viruddha_ahaara
              : [],
            family_history: Array.isArray(modelObj.family_history) ? modelObj.family_history : [],
            known_diseases: Array.isArray(modelObj.known_diseases) ? modelObj.known_diseases : [],
            commonly_eaten_food: Array.isArray(modelObj.commonly_eaten_food)
              ? modelObj.commonly_eaten_food
              : [],
            ordered_investigations: Array.isArray(modelObj.ordered_investigations)
              ? modelObj.ordered_investigations
              : [],
            suggestive_slokas: Array.isArray(modelObj.suggestive_slokas)
              ? modelObj.suggestive_slokas
              : [],
          };
          handleCDSSData(data);
        }

        const clinicianObj: any = objs.find(
          (o: any) => o && (o.ordered_tests_or_actions || o.prescribed_medications)
        );
        if (clinicianObj) {
          if (Array.isArray(clinicianObj.ordered_tests_or_actions)) {
            setModelInvestigations(clinicianObj.ordered_tests_or_actions.slice(0, 5));
          }
        }

        return;
      }
    },
    [handleCDSSData]
  );

  // Snapshot the recording duration the moment the leave-confirmation modal
  // opens, so its detail row shows an accurate "as of now" time without
  // needing AudioRecorder to report duration on every tick.
  const openLeaveConfirm = useCallback(() => {
    setLeaveConfirmDurationSeconds(audioRecorderRef.current?.getDurationSeconds() ?? 0);
    setShowLeaveConfirm(true);
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (hasUnsavedSession) {
      openLeaveConfirm();
      return;
    }
    navigate('/dashboard');
  }, [navigate, hasUnsavedSession, openLeaveConfirm]);

  // Intercept the browser back button (and tab close) while there's an
  // unsaved, in-progress recording so it isn't lost silently.
  useBackNavigationGuard(hasUnsavedSession, openLeaveConfirm);

  const handleStayOnPage = useCallback(() => {
    setShowLeaveConfirm(false);
  }, []);

  const handleExitWithoutSaving = useCallback(() => {
    setShowLeaveConfirm(false);
    // Discard both copies of the recording: the in-memory audioStore (which
    // useConsultationRecovery checks first on a later visit) and the
    // IndexedDB backup — clearing only one would let the "discarded"
    // recording resurface next time this consultation is opened.
    useAudioStore.getState().clearAudioRecording(consultationId!);
    // Best-effort — don't block navigation on this.
    clearConsultationData(consultationId!).catch(() => {});

    // Mark the backend records as cancelled rather than leaving them
    // looking like an active consultation forever — the Encounter was
    // already flipped to 'in-progress' on mount (startConsultationMutation
    // above), and the Appointment is typically already 'checked-in' from
    // the earlier vitals/check-in step. Fire-and-forget so a failure here
    // never blocks the user from leaving; invalidate on success so the
    // dashboard doesn't show a stale "checked-in" row.
    const invalidateDashboardQueries = () => {
      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
    };
    if (consultationId && !consultationId.startsWith('session-')) {
      medplumResourcesApi
        .updateEncounter(consultationId, { status: 'cancelled' })
        .then(invalidateDashboardQueries)
        .catch((error) => {
          logger.error('Failed to cancel encounter on exit without saving:', error);
        });
    }
    if (effectiveAppointmentId) {
      appointmentsApi
        .cancelAppointment(effectiveAppointmentId, 'Consultation exited without saving')
        .then(invalidateDashboardQueries)
        .catch((error) => {
          logger.error('Failed to cancel appointment on exit without saving:', error);
        });
    }

    navigate('/dashboard', { replace: true });
  }, [navigate, consultationId, effectiveAppointmentId, queryClient]);

  const handleSaveDraftAndExitClick = useCallback(() => {
    setShowLeaveConfirm(false);
    // handleSaveDraft (passed to AudioRecorder as onSaveDraft) already
    // navigates to /dashboard once the async summary arrives and the
    // draft is persisted — nothing further to do here.
    audioRecorderRef.current?.saveDraft();
  }, []);

  // Handle session summary from LiveKit agent
  const handleSessionSummary = useCallback(
    async (sessionInfo: SessionInfoResponse) => {
      if (!sessionInfo.success || !sessionInfo.summary) {
        logger.error('Session summary failed or missing data');
        toast.error('Failed to generate consultation summary');
        return;
      }

      setIsFinalizingSummary(true);

      const summary = sessionInfo.summary;
      const cdssData = summary.pranik_cdss;
      const extractedCdssSuggestions: CDSSSuggestion[] = [];

      if (cdssData?.betterMedicationSuggestions?.value) {
        cdssData.betterMedicationSuggestions.value.forEach((suggestion: any, index: number) => {
          const isAddition = suggestion.to_replace === 'add' || suggestion.to_replace === 'ADD';
          const title = isAddition
            ? `Recommended: ${suggestion.medication}`
            : `Alternative for ${suggestion.to_replace}`;

          const confidence =
            suggestion.flag === 'red'
              ? 0.9
              : suggestion.flag === 'yellow'
                ? 0.7
                : suggestion.flag === 'blue'
                  ? 0.6
                  : 0.5;

          extractedCdssSuggestions.push({
            id: `better-med-${index}`,
            type: 'medication',
            title: title,
            description: suggestion.explanation || '',
            confidence: confidence,
            reasoning: `Suggested: ${suggestion.medication}`,
            references: suggestion.source_url ? [suggestion.source_url] : [],
          });
        });
      }

      if (cdssData?.missedSymptomAlerts?.value) {
        cdssData.missedSymptomAlerts.value.forEach((alert: any, alertIndex: number) => {
          const severity = alert.severity?.toLowerCase();
          // If there are suggested medications, create a suggestion for each
          if (alert.suggested_medications && alert.suggested_medications.length > 0) {
            alert.suggested_medications.forEach((med: any, medIndex: number) => {
              extractedCdssSuggestions.push({
                id: `missed-symptom-${alertIndex}-${medIndex}`,
                type: 'medication',
                title: `Missed Symptom: ${alert.symptom}`,
                description: med.explanation || med.reason || '',
                confidence:
                  severity === 'severe' || severity === 'high'
                    ? 0.9
                    : severity === 'moderate'
                      ? 0.7
                      : 0.5,
                reasoning: `Suggested: ${med.medication}`,
                references: med.source_url ? [med.source_url] : [],
              });
            });
          } else {
            // If no suggested medications, still show the missed symptom alert
            extractedCdssSuggestions.push({
              id: `missed-symptom-${alertIndex}`,
              type: 'warning',
              title: `Missed Symptom: ${alert.symptom}`,
              description: alert.explanation || alert.reason || '',
              confidence:
                severity === 'severe' || severity === 'high'
                  ? 0.9
                  : severity === 'moderate'
                    ? 0.7
                    : 0.5,
              reasoning: `Symptom not addressed`,
              references: alert.source_url ? [alert.source_url] : [],
            });
          }
        });
      }

      if (cdssData?.drugInteractionAlerts?.value) {
        cdssData.drugInteractionAlerts.value.forEach((alert: any, index: number) => {
          extractedCdssSuggestions.push({
            id: `drug-interaction-${index}`,
            type: 'warning',
            title: 'Drug Interaction Alert',
            description: alert.explanation || '',
            confidence: 0.9,
            reasoning: alert.suggested_alternative || '',
            references: alert.source_url ? [alert.source_url] : [],
          });
        });
      }

      if (cdssData?.allergyConflictAlerts?.value) {
        cdssData.allergyConflictAlerts.value.forEach((alert: any, index: number) => {
          extractedCdssSuggestions.push({
            id: `allergy-conflict-${index}`,
            type: 'warning',
            title: 'Allergy Conflict Alert',
            description: alert.explanation || '',
            confidence: 0.9,
            reasoning: alert.suggested_alternative || '',
            references: alert.source_url ? [alert.source_url] : [],
          });
        });
      }

      if (latestCDSSDataRef.current && consultationId && patientId) {
        try {
          await draftsApi.saveDraft(
            consultationId,
            user?.id || '',
            patientId,
            latestCDSSDataRef.current
          );
        } catch (e) {
          logger.warn('Failed to pre-save draft before case sheet navigation:', e);
        }
      }

      if (consultationId && user?.id && patientId) {
        try {
          const payload = buildCaseSheetPayloadFromSession(
            consultationId,
            user.id,
            patientId,
            summary
          );
          await caseSheetSummaryApi.saveSummary(payload);
        } catch (e) {
          logger.error('Failed to save case sheet summary to DB:', e);
          logger.error(
            '[CaseSheet] Payload that failed to save:',
            buildCaseSheetPayloadFromSession(consultationId, user.id, patientId, summary)
          );
          // Don't block navigation — CaseSheetPage will show what it has
        }
      }

      // Extract suggestive slokas from final Ayurveda summary and show in right panel
      // before navigating, so they are visible during the brief auto-save period
      if (isAyurveda) {
        logger.log('Session summary payload:', summary);
        logger.log('Session summary suggestive slokas:', summary.suggestive_slokas);
      }
      if (Array.isArray(summary.suggestive_slokas) && summary.suggestive_slokas.length > 0) {
        setReferenceSlokas(summary.suggestive_slokas);
      }

      // Give the live agent a short grace period to deliver any final update
      // (e.g. treatment/medicines said right as the doctor hit Submit) before
      // we snapshot data for the Case Sheet — otherwise the async saves above
      // can race ahead of that last update and it never makes it across.
      await new Promise((resolve) => setTimeout(resolve, 1200));

      navigate(`/case-sheet/${consultationId}`, {
        replace: true,
        state: {
          sessionSummary: summary,
          cdssSuggestions: extractedCdssSuggestions,
          // Astha Sthaana Pareeksha/Prakruthi/Dosha/Aama chip values exactly as
          // shown on this page's Ayurvedic Assessment card (live entries +
          // kiosk-snapshot fallback already merged) — the Case Sheet reuses
          // this directly instead of re-deriving it from a different resource.
          // Read from the ref (not the closure variable) so any update that
          // arrived during the awaited saves/grace period above is included.
          clinicalAssessment: effectiveClinicalAssessmentRef.current,
          // Structured medicines table exactly as shown on this page's
          // Treatment card, same reasoning as clinicalAssessment above.
          prescribedMedicines: prescribedMedicinesRef.current,
          // Duration/Mode of Onset exactly as shown on this page's Patient
          // Information card, same reasoning as clinicalAssessment above.
          duration: effectiveAyurvedaDataRef.current?.duration,
          modeOfOnset: effectiveAyurvedaDataRef.current?.modeOfOnset,
          // History/Personal History exactly as shown on this page's Patient
          // Information card, same reasoning as clinicalAssessment above.
          pastIllness: effectiveAyurvedaDataRef.current?.pastIllness,
          treatmentHistory: effectiveAyurvedaDataRef.current?.treatmentHistory,
          historyFamilyHistory: effectiveAyurvedaDataRef.current?.historyFamilyHistory,
          currentMedication: effectiveAyurvedaDataRef.current?.currentMedication,
          familySocialOccupational: effectiveAyurvedaDataRef.current?.familySocialOccupational,
          personalHistory: effectiveAyurvedaDataRef.current?.personalHistory,
          // Foods the patient regularly eats arrive as their own stream, not
          // inside personalHistory — the consultation page folds them into
          // Diet, so the Case Sheet needs them too or Diet comes out blank.
          commonlyEatenFood: effectiveAyurvedaDataRef.current?.commonlyEatenFood,
        },
      });
    },
    [
      navigate,
      consultationId,
      patientId,
      user?.id,
      isAyurveda,
      effectiveClinicalAssessment,
      effectiveAyurvedaData?.duration,
      effectiveAyurvedaData?.modeOfOnset,
      effectiveAyurvedaData?.pastIllness,
      effectiveAyurvedaData?.treatmentHistory,
      effectiveAyurvedaData?.historyFamilyHistory,
      effectiveAyurvedaData?.currentMedication,
      effectiveAyurvedaData?.familySocialOccupational,
      effectiveAyurvedaData?.personalHistory,
    ]
  );

  const hasError = isErrorEncounter || isErrorPatient;

  // Only show error state if there's an actual error (not just loading)
  if (hasError) {
    return (
      <AppLayout>
        <div className="p-6">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <p className="mb-4 text-destructive">Failed to load consultation</p>
              <Button onClick={handleBack} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Records
              </Button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  const patientDataForHeader: PatientData | null = activePatient
    ? {
        name: activePatient.patient.name,
        identifier: activePatient.patient.identifier,
        age: activePatient.patient.age,
        gender: activePatient.patient.gender,
        birthDate: patient?.birthDate,
      }
    : null;

  const chiefComplaintSymptoms = isAyurveda
    ? summarizeChiefComplaint(effectiveAyurvedaData?.chiefComplaint)
    : [];
  const displaySymptoms = mergeSymptoms(symptoms, chiefComplaintSymptoms);

  const headerTitle = activePatient?.patient?.name
    ? `Consultation - ${activePatient.patient.name}`
    : 'Consultation';

  return (
    <AppLayout
      patientData={patientDataForHeader}
      patientId={patientId}
      encounterId={effectiveEncounterId}
      appointmentId={effectiveAppointmentId || undefined}
    >
      <ConsultationView
        header={{
          title: headerTitle,
          patientName: activePatient?.patient.name ?? '',
          patientIdentifier: activePatient?.patient.identifier ?? '',
          status: isFinished
            ? 'completed'
            : encounter?.status === 'in-progress'
              ? 'in-progress'
              : 'draft',
        }}
        sessionMode={false}
        patientId={patientId}
        actions={{
          onBack: handleBack,
          onComplete: () => navigate(`/case-sheet/${consultationId}`),
          completeDisabled: false,
          completePending: false,
        }}
        patient={activePatient?.patient}
        vitals={encounterVitals}
        leftPanel={{
          audioRecorder: (
            <AudioRecorder
              ref={audioRecorderRef}
              consultationId={consultationId!}
              patientId={patientId}
              authToken={token || ''}
              disabled={isFinished}
              autoStart={!isFinished && !isLoadingEncounter && !isLoadingVitals && !isLoadingSoap}
              isContinuation={isContinuation}
              previousSummary={previousSummaryRaw}
              vitals={encounterVitals}
              soapData={soapData}
              onCDSSSuggestion={handleCDSSSuggestion}
              onCDSSData={handleCDSSData}
              onRecordingStateChange={(recording, paused) => {
                setIsRecording(recording);
                setIsPaused(paused);
              }}
              onSubmittingChange={setIsSubmittingAudio}
              onSessionSummary={handleSessionSummary}
              onSaveDraft={handleSaveDraft}
              isSavingDraft={isSavingDraft}
              onSaveRecord={handleSaveRecord}
              onQuickSubmit={handleQuickSubmit}
              onLiveKitClientReady={handleLiveKitClientReady}
            />
          ),
          vitalsForm: (
            <VitalsForm
              initialVitals={encounterVitals ?? undefined}
              onSubmit={updateVitalsMutation.mutate}
              isLoading={updateVitalsMutation.isPending}
            />
          ),
          soapData: soapData ?? null,
          soapLoading: isLoadingEncounter || isLoadingSoap,
          ayurvedaData: effectiveAyurvedaData,
          clinicalAssessment: effectiveClinicalAssessment,
          onClinicalAssessmentOverride: handleClinicalAssessmentOverride,
          pendingClinicalAssessmentParameter,
        }}
        middle={{
          suggestions: modelSuggestions ?? [],
          investigations: modelInvestigations ?? [],
          diagnoses: modelDiagnoses ?? [],
          diffDiagnoses: modelDiffDiagnoses ?? [],
        }}
        right={{
          symptoms: displaySymptoms ?? [],
          doctorInvestigations: [],
          prescribedMedications: prescribedMedications ?? [],
          suggestedMedications: suggestedMedications ?? [],
          dietRecommendations: dietRecommendations ?? [],
          lifestyleRecommendations: lifestyleRecommendations ?? [],
          viruddhaAhaara: viruddhaAhaara ?? [],
          referenceSlokas: referenceSlokas ?? [],
        }}
        isAyurveda={isAyurveda}
      />
      <LeaveConsultationModal
        open={showLeaveConfirm}
        patientName={activePatient?.patient.name}
        isRecording={isRecording}
        isSavingDraft={isSavingDraft}
        recordingSeconds={leaveConfirmDurationSeconds}
        symptomsCount={displaySymptoms.length}
        diagnosesCount={modelDiagnoses.length}
        onExitWithoutSaving={handleExitWithoutSaving}
        onSaveDraftAndExit={handleSaveDraftAndExitClick}
        onStay={handleStayOnPage}
      />
    </AppLayout>
  );
}
